import React from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7 }
};

const About = () => {
  return (
    <div data-testid="about-page" className="min-h-screen bg-[#F4F4F4]">
      {/* 1. Hero - Dark full screen */}
      <section className="bg-[#641b4d] min-h-screen flex items-center px-6">
        <div className="max-w-5xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.3em] text-[#bc64aa] mb-8"
          >
            About Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="about-heading"
            className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tighter text-white leading-[1.05]"
          >
            At Veveru, we are building a new way to think about materials.
          </motion.h1>
        </div>
      </section>

      {/* 2. The Observation - Full width text */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-10">
            The Observation
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl lg:text-4xl font-light text-[#641b4d] leading-snug tracking-tight mb-8">
            We started with a simple observation — most everyday products aren't built to last.
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl lg:text-4xl font-light text-[#641b4d] leading-snug tracking-tight mb-8">
            And even when they look good, they don't stay relevant for long.
          </motion.p>
          <motion.p {...fadeUp} className="text-lg text-[#7a5e75] leading-relaxed">
            And almost never with impact built into the system. We saw an opportunity to do this differently.
          </motion.p>
        </div>
      </section>

      {/* 3. Materials image - Full bleed - use a real product shot */}
      <section className="relative h-[70vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/5ui82gy4_P_85.png"
          alt="Premium white ribbed FRP planter in luxury living room"
          className="w-full h-full object-cover"
        />
      </section>

      {/* 4. What We Are */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-10">
            What We Do
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl font-light text-[#641b4d] leading-snug tracking-tight mb-8">
            Veveru is a composite technology company focused on creating products that are structurally superior, aesthetically refined, and designed for scale.
          </motion.p>
          <motion.p {...fadeUp} className="text-lg text-[#7a5e75] leading-relaxed mb-6">
            Our approach begins at the material level — engineering solutions that are lightweight, durable, and efficient by design.
          </motion.p>
          <motion.p {...fadeUp} className="text-lg text-[#7a5e75] leading-relaxed">
            One of our earliest applications of this thinking is in the form of FRP planters.
          </motion.p>
        </div>
      </section>

      {/* 5. FRP Texture - Full bleed with overlay */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/d1dcf077-4e7b-4264-9d73-3de5a1021d6b/images/d5f2550e7cfde6855f678d2580e46a81d5ac0f0033103f6ebf87d98542f98ec4.png"
          alt="FRP composite material"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#641b4d]/50 flex items-center justify-center px-6">
          <motion.p
            {...fadeUp}
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white text-center tracking-tight leading-tight max-w-3xl"
          >
            Structurally superior. Aesthetically refined. Designed for scale.
          </motion.p>
        </div>
      </section>

      {/* 6. The Evolution */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-10">
            The Evolution
          </motion.p>
          <motion.p {...fadeUp} className="text-2xl md:text-3xl font-light text-[#641b4d] leading-snug tracking-tight mb-8">
            What started as a material exploration quickly evolved into something larger — a distributed production model led by women.
          </motion.p>
          <motion.p {...fadeUp} className="text-lg text-[#7a5e75] leading-relaxed mb-6">
            Where technical training, manufacturing, and market access come together.
          </motion.p>
          <motion.p {...fadeUp} className="text-lg text-[#641b4d] leading-relaxed font-light">
            It is core to how we build.
          </motion.p>
        </div>
      </section>

      {/* 7. Women at work - Side by side */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="h-[50vh] md:h-[70vh] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/v6rocqk9_IMG_7440.jpeg"
              alt="Woman working with planter mould"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="h-[50vh] md:h-[70vh] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/j0wfkofr_IMG_20260326_110526.jpg.jpeg"
              alt="Gelcoat application on planter mould"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* 8. Impact Philosophy - Bold dark */}
      <section className="bg-[#641b4d] py-28 lg:py-36 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.p
            {...fadeUp}
            className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-snug tracking-tight"
          >
            We believe impact should not sit outside the business — it should be embedded into how products are made, who makes them, and how they reach the market.
          </motion.p>
        </div>
      </section>

      {/* 9. Building Systems - 4 pillars with large images */}
      <section className="py-24 lg:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#bc64aa] mb-6">Today</p>
            <p className="text-2xl md:text-3xl font-light text-[#641b4d] leading-snug tracking-tight">
              We are building systems that can scale.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { title: 'Advanced Materials', desc: 'at the core', img: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/1n76lptc_4.jpg.jpeg' },
              { title: 'Structured Production', desc: 'processes', img: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/no0q98qj_IMG_20260326_090614.jpg.jpeg' },
              { title: 'Decentralized Manufacturing', desc: 'skilled units', img: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/0tm8s85r_1.png' },
              { title: 'Design-Led Products', desc: 'for modern markets', img: 'https://customer-assets.emergentagent.com/job_pot-commerce-1/artifacts/d1r29g87_P_24.png' }
            ].map((item, index) => (
              <motion.div
                key={index}
                data-testid={`about-pillar-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group relative overflow-hidden"
              >
                <img src={item.img} alt={item.title} className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white text-sm font-light mb-0.5">{item.title}</h3>
                  <p className="text-white/70 text-xs">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Closing - tight below pillars */}
          <motion.div {...fadeUp} className="mt-16 text-center">
            <p className="text-lg text-[#7a5e75] mb-4">
              We are not just manufacturing products.
            </p>
            <p className="text-3xl md:text-4xl lg:text-5xl font-light text-[#641b4d] tracking-tight leading-tight">
              We are building a material-driven, impact-embedded, design-forward company.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
